/** The melds on the table. Public by definition — they are face up. scope.md §3.2. */

import type { Card, Meld, PlayerView } from '@garbage/shared';
import { isJoker } from '@garbage/shared';
import { CardFace } from './Card.js';
import type { PackId } from '../packs.js';
import { useT } from '../i18n.js';

export function jokerStandsFor(meld: Meld, card: Card): string | null {
  const stands = meld.jokers.find((j) => j.cardId === card.id);
  if (!stands) return null;
  return stands.suit ? `${stands.rank}${suitGlyph(stands.suit)}` : stands.rank;
}

function suitGlyph(suit: string): string {
  return { spades: '♠', hearts: '♥', diamonds: '♦', clubs: '♣' }[suit] ?? '';
}

export function Melds({
  view, pack, onLayOff, onSwapJoker, selectedCardId, layOffTargets, swapTargets,
}: {
  view: PlayerView;
  pack: PackId;
  selectedCardId: string | null;
  /** Meld ids the selected card could be added to. */
  layOffTargets: string[];
  /** Meld ids holding a joker this player could take. */
  swapTargets: string[];
  onLayOff: (meldId: string) => void;
  onSwapJoker: (meldId: string) => void;
}) {
  const t = useT();
  if (view.melds.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-sm text-bone/40" data-testid="melds-empty">
        {t('melds.empty')}
      </p>
    );
  }

  /*
   * Compact, so the table rarely needs scrolling. Each meld's cards overlap like a fan
   * with the rank and suit showing on the visible edge, and a wide screen fits several
   * melds side by side. The whole meld is still one button, so laying off and taking a
   * joker work exactly as before. Ben's call, 16 September 2026.
   */
  return (
    <div
      className="grid grid-cols-[repeat(auto-fill,minmax(19rem,1fr))] gap-1.5 px-3 py-2"
      data-testid="melds"
    >
      {view.melds.map((meld) => {
        const owner = view.players.find((p) => p.id === meld.ownerId);
        const canLayOff = selectedCardId !== null && layOffTargets.includes(meld.id);
        const canSwap = swapTargets.includes(meld.id);
        const actionable = canLayOff || canSwap;

        return (
          <button
            key={meld.id}
            type="button"
            data-testid={`meld-${meld.id}`}
            disabled={!actionable}
            onClick={() => (canLayOff ? onLayOff(meld.id) : onSwapJoker(meld.id))}
            className={[
              'flex min-w-0 items-center gap-2 rounded-xl px-2 pb-2 pt-1.5 text-left',
              actionable ? 'bg-sun/20 ring-2 ring-sun' : 'bg-black/25',
            ].join(' ')}
          >
            <span className="w-10 shrink-0 truncate text-[10px] text-bone/50">
              {owner?.name ?? '—'}
            </span>
            <span className="flex">
              {meld.cards.map((card, i) => (
                // A long run overlaps a little more, so round 7 still fits a phone.
                <span key={card.id} className={i === 0 ? 'relative' : meld.cards.length > 7 ? 'relative -ml-4' : 'relative -ml-3'}>
                  <CardFace card={card} size="small" corner marked={isJoker(card)} pack={pack} />
                  {isJoker(card) && (
                    // On the visible edge of the card, so the next card never hides it.
                    <span
                      data-testid={`joker-${card.id}`}
                      className="absolute -bottom-1.5 left-0 z-10 rounded bg-violet-600
                                 px-0.5 text-[9px] font-bold leading-tight text-white"
                    >
                      {jokerStandsFor(meld, card)}
                    </span>
                  )}
                </span>
              ))}
            </span>
            {canSwap && !canLayOff && (
              <span className="ml-auto text-[10px] font-bold text-sun">{t('melds.takeJoker')}</span>
            )}
            {canLayOff && <span className="ml-auto text-[10px] font-bold text-sun">{t('melds.addHere')}</span>}
          </button>
        );
      })}
    </div>
  );
}
